import { Badge, Box, Button, Group, Modal, Radio, ScrollArea, Stack, Text, TextInput } from "@mantine/core"
import { MagnifyingGlassIcon, PlusIcon } from "@phosphor-icons/react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import useSWR from "swr"
import { DeliveryRouteApi } from "../../apis"
import { IDeliveryNotePlus } from "../../models/DeliveryNote"
import { message } from "../../utils/message"
import { DeliveryActionButton } from "../delivery/action-controls"
import { Dot } from "../delivery/atoms"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"
import classes from "./add-to-route.module.css"

interface Props {
  opened: boolean
  onClose: () => void
  selectedDns: IDeliveryNotePlus[]
  onSuccess: () => void
}

/** «Маршрутад нэмэх» (canvas Ord_DeliveryModals 2): pick a PLANNED route, POST add-stops (DEL_ROUTE_PLAN). */
export function AddToRouteDialog({ opened, onClose, selectedDns, onSuccess }: Props) {
  const { t } = useTranslation("delivery")
  const navigate = useNavigate()
  const { permissionState } = useDeliveryActions()
  const perm = permissionState("addToRoute")
  const [pickedRouteId, setPickedRouteId] = useState<string | null>(null)
  const [search, setSearch] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useSWR(
    opened ? "add-to-route.planned-routes" : null,
    () => DeliveryRouteApi.list({ filter: { status: "PLANNED" }, offset: { limit: 50, skip: 0 } }),
    { revalidateOnFocus: false },
  )
  const routes = useMemo(() => data?.rows ?? [], [data?.rows])

  const filtered = useMemo(() => {
    const n = search.trim().toLowerCase()
    if (!n) return routes
    return routes.filter((r) => r.routeNumber?.toLowerCase().includes(n) || (r.name ?? "").toLowerCase().includes(n))
  }, [routes, search])

  const handleClose = () => {
    setError(null)
    setPickedRouteId(null)
    setSearch("")
    onClose()
  }

  const handleSubmit = async () => {
    if (!pickedRouteId || selectedDns.length === 0) return
    setSubmitting(true)
    setError(null)
    try {
      await DeliveryRouteApi.addStops(pickedRouteId, {
        stops: selectedDns
          .filter((dn) => dn.id)
          .map((dn) => ({
            deliveryNoteId: dn.id as string,
            address: (dn as { receiverAddress?: string | null }).receiverAddress ?? null,
          })),
      })
      message.success(t("manage.addToRoute.success"))
      onSuccess()
      handleClose()
    } catch (err) {
      // O11: BE answers `error.route.<key>[:ids]`; show the translated sentence, never the raw key.
      setError(translateBackendError(err, "error.route.add_stops_failed"))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Box>
          <Text fw={650} fz="var(--nw-fz-drawer)">
            {t("manage.addToRoute.title")}
          </Text>
          <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
            {t("manage.addToRoute.subtitle", { count: selectedDns.length })}
          </Text>
        </Box>
      }
      size={440}
      centered
    >
      <Stack gap="md">
        {routes.length > 6 && (
          <TextInput
            placeholder={t("manage.addToRoute.search")}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            leftSection={<MagnifyingGlassIcon size={16} />}
          />
        )}

        <Radio.Group
          label={t("manage.addToRoute.route")}
          value={pickedRouteId}
          onChange={(v) => {
            setPickedRouteId(v)
            setError(null)
          }}
        >
          <ScrollArea.Autosize mah={300} type="hover" mt="xs">
            {isLoading ? (
              <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                {t("manage.state.loading")}
              </Text>
            ) : filtered.length === 0 ? (
              <Stack gap="sm" align="flex-start">
                <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                  {search.trim() ? t("manage.addToRoute.noMatch") : t("manage.addToRoute.noRoutes")}
                </Text>
                {!search.trim() && (
                  <DeliveryActionButton
                    state={permissionState("createRoute")}
                    variant="default"
                    leftSection={<PlusIcon size={16} />}
                    onClick={() => {
                      handleClose()
                      navigate("/delivery-routes/new")
                    }}
                  >
                    {t("manage.action.createRoute")}
                  </DeliveryActionButton>
                )}
              </Stack>
            ) : (
              <Stack gap="xs">
                {filtered.map((route) => (
                  <Radio.Card key={route.id} value={route.id} className={classes.card}>
                    <Group wrap="nowrap" align="center" gap="sm">
                      <Radio.Indicator />
                      <Box>
                        <Text fw={600} fz="var(--nw-fz-body)">
                          {route.name?.trim() || route.routeNumber}
                        </Text>
                        <Text ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text-2)">
                          {route.routeNumber}
                        </Text>
                      </Box>
                    </Group>
                  </Radio.Card>
                ))}
              </Stack>
            )}
          </ScrollArea.Autosize>
        </Radio.Group>

        <Group gap="xs">
          {selectedDns.slice(0, 6).map((dn) => (
            <Badge key={dn.id ?? dn.refCode} variant="light" color="gray" radius="sm" ff="monospace" tt="none" size="lg">
              {dn.refCode}
            </Badge>
          ))}
          {selectedDns.length > 6 && (
            <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
              +{selectedDns.length - 6}
            </Text>
          )}
        </Group>

        {error && (
          <Group role="alert" className={classes.error} gap="sm" wrap="nowrap" align="flex-start">
            <Box mt={6}>
              <Dot tone="red" />
            </Box>
            <Text fz="var(--nw-fz-body)">{error}</Text>
          </Group>
        )}

        <Stack gap={4} align="flex-end">
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleClose} disabled={submitting}>
              {t("manage.common.cancel")}
            </Button>
            <DeliveryActionButton
              state={perm}
              color="dark"
              onClick={handleSubmit}
              loading={submitting}
              disabledReason={!pickedRouteId ? t("manage.addToRoute.pickRoute") : undefined}
            >
              {t("manage.addToRoute.submit")}
            </DeliveryActionButton>
          </Group>
          {!perm.allowed && (
            <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
              {perm.reason}
            </Text>
          )}
        </Stack>
      </Stack>
    </Modal>
  )
}
